import { withSentry, setUser } from "@sentry/remix";
import type { LinksFunction, MetaFunction } from "@remix-run/node";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
} from "@remix-run/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import tailwindStyles from "@/app/styles/tailwind.css?url";
import sonnerStyles from "sonner/dist/styles.css?url";
import { Toaster } from "@/app/components/ui/sonner";
// Importing the i18n module initializes the shared client instance. The language
// is resolved from the cookie at init (client) and per-request in
// entry.server.tsx (server), so server and client render the same language.
import { DEFAULT_LANGUAGE } from "@/app/utils/i18n";
import { useTranslation } from "react-i18next";
import { RouteErrorBoundary } from "@/app/components/ErrorBoundary";
import { getEnv } from "@/server/lib/env";
import {
  PROD_HOSTNAME,
  GOOGLE_ADS_ID,
  GOOGLE_ADS_SEND_TO,
  LINKEDIN_PARTNER_ID,
  LINKEDIN_CONVERSION_IDS,
} from "@/app/marketing/constants";
import { getQueuedEvents } from "@/app/marketing/tracking.server";
import { getLoggedInUser } from "@/app/utils/remix-helpers.server";
import ky from "ky";
import { Footer } from "@/app/components/layout/Footer";

export const meta: MetaFunction = () => [
  { title: "AiAuditBuddy" },
  { charSet: "utf-8" },
];

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: tailwindStyles },
  { rel: "stylesheet", href: sonnerStyles },
];

export const loader = async ({ request }: { request: Request }) => {
  const ENV = getEnv();
  const url = new URL(request.url);

  // Check for force production cookie (for testing)
  const cookieHeader = request.headers.get("Cookie");
  const forceProdCookie = cookieHeader
    ?.split(";")
    .find(c => c.trim().startsWith("__force_prod="))
    ?.split("=")[1]
    ?.trim();

  const isProd = forceProdCookie === "true" || url.hostname === PROD_HOSTNAME;
  console.log("[TRACKING DEBUG] URL hostname:", url.hostname);
  console.log("[TRACKING DEBUG] PROD_HOSTNAME:", PROD_HOSTNAME);
  console.log("[TRACKING DEBUG] isProd:", isProd);
  console.log("[TRACKING DEBUG] forceProdCookie:", forceProdCookie);
  const trkEvents = isProd ? await getQueuedEvents(request) : [];
  console.log(
    "[TRACKING DEBUG] Retrieved trkEvents:",
    trkEvents.length,
    "events",
    trkEvents
  );

  // Get user for identify (only public user ID, no PII)
  const user = await getLoggedInUser(request);
  const userId = user?.id || null;
  console.log("[TRACKING DEBUG] User ID for identification:", userId);

  return {
    ENV,
    isProd,
    trkEvents,
    userId,
  };
};

function App() {
  const { ENV, isProd, trkEvents, userId } = useLoaderData<typeof loader>();
  const isLoggedIn = !!userId;

  // Reflect the rendered language on <html lang>. Both server and client resolve
  // i18n to the cookie's language, so this matches and won't cause a mismatch.
  const { i18n } = useTranslation();
  const lang = i18n.language || DEFAULT_LANGUAGE;

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
            retry: 1,
          },
        },
      })
  );

  // Attribute client-side Sentry events to the user (id only, no PII) so errors
  // are identifiable instead of landing as anonymous "0 users" issues.
  useEffect(() => {
    setUser(userId ? { id: userId } : null);
  }, [userId]);

  // Identify user in Google Ads (on first load after signup/login)
  useEffect(() => {
    console.log(
      "[TRACKING DEBUG] User identification effect - isProd:",
      isProd,
      "userId:",
      userId
    );
    if (!isProd || !userId) {
      console.log(
        "[TRACKING DEBUG] Skipping user identification - not prod or no userId"
      );
      return;
    }

    // Wait for gtag to be available
    const waitForGtag = (): Promise<void> => {
      return new Promise(resolve => {
        if (typeof window.gtag !== "undefined") {
          resolve();
          return;
        }
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds max
        const checkInterval = setInterval(() => {
          attempts++;
          if (typeof window.gtag !== "undefined") {
            clearInterval(checkInterval);
            resolve();
          } else if (attempts >= maxAttempts) {
            clearInterval(checkInterval);
            resolve(); // Resolve anyway
          }
        }, 100);
      });
    };

    const identify = async () => {
      console.log("[TRACKING DEBUG] Starting user identification process...");
      await waitForGtag();
      if (typeof window.gtag !== "undefined") {
        console.log(
          "[TRACKING DEBUG] Configuring Google Ads with user ID:",
          userId
        );
        window.gtag("config", GOOGLE_ADS_ID, {
          user_id: userId,
        });
        console.log(
          "[TRACKING DEBUG] User identification completed successfully"
        );
      } else {
        console.log(
          "[TRACKING DEBUG] gtag not available for user identification"
        );
      }
    };

    identify();
  }, [isProd, userId]);

  // Client-side event flusher
  useEffect(() => {
    console.log(
      "[TRACKING DEBUG] Event flusher effect - isProd:",
      isProd,
      "trkEvents:",
      trkEvents?.length || 0,
      "events"
    );
    if (!isProd || !trkEvents || trkEvents.length === 0) {
      console.log(
        "[TRACKING DEBUG] Skipping event flushing - not prod or no events"
      );
      return;
    }
    console.log("[TRACKING DEBUG] Events to flush:", trkEvents);

    // Wait for at least one provider (gtag or lintrk) to be available
    const waitForProviders = (): Promise<void> => {
      return new Promise(resolve => {
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds max (50 * 100ms)
        const checkInterval = setInterval(() => {
          attempts++;
          const hasGtag = typeof window.gtag !== "undefined";
          const hasLintrk = typeof window.lintrk !== "undefined";

          // Proceed if at least one provider is available
          if (hasGtag || hasLintrk) {
            clearInterval(checkInterval);
            resolve();
          } else if (attempts >= maxAttempts) {
            clearInterval(checkInterval);
            resolve(); // Resolve anyway to proceed
          }
        }, 100);
      });
    };

    const flushEvents = async () => {
      console.log("[TRACKING DEBUG] Starting event flush process...");
      await waitForProviders();
      console.log(
        "[TRACKING DEBUG] Providers available - gtag:",
        typeof window.gtag !== "undefined",
        "lintrk:",
        typeof window.lintrk !== "undefined"
      );

      const deliveredIds: string[] = [];

      for (const event of trkEvents) {
        console.log("[TRACKING DEBUG] Processing event:", event);
        let attemptedDelivery = false;

        // Google Ads (always active on prod)
        if (typeof window.gtag !== "undefined") {
          console.log("[TRACKING DEBUG] Google Ads tracking available");
          attemptedDelivery = true;
          let eventLabel: string = event.eventType;
          if (event.props) {
            const parts: string[] = [event.eventType];
            if (event.props.planName) {
              parts.push(event.props.planName);
            }
            if (event.props.seats) {
              parts.push(String(event.props.seats));
            }
            eventLabel = parts.join("-");
            console.log(
              "[TRACKING DEBUG] Built event label from props:",
              eventLabel,
              "props:",
              event.props
            );
          }

          console.log("[TRACKING DEBUG] Sending Google Ads conversion event:", {
            eventId: event.id,
            eventType: event.eventType,
            send_to: GOOGLE_ADS_SEND_TO,
            event_label: eventLabel,
            full_event: event,
          });

          // trigger conversion
          window.gtag("event", "conversion", {
            send_to: GOOGLE_ADS_SEND_TO,
            event_label: eventLabel,
            value: 1.0,
            currency: "EUR",
          });
          console.log(
            "[TRACKING DEBUG] Google Ads conversion event sent successfully"
          );
        } else {
          console.log("[TRACKING DEBUG] Google Ads gtag not available");
        }

        // LinkedIn (skip if conversion ID is null)
        if (typeof window.lintrk !== "undefined") {
          console.log("[TRACKING DEBUG] LinkedIn tracking available");
          const conversionId =
            LINKEDIN_CONVERSION_IDS[
              event.eventType as keyof typeof LINKEDIN_CONVERSION_IDS
            ];
          console.log(
            "[TRACKING DEBUG] LinkedIn conversion ID for",
            event.eventType,
            ":",
            conversionId
          );
          if (conversionId !== null) {
            console.log("[TRACKING DEBUG] Sending LinkedIn conversion event:", {
              eventId: event.id,
              eventType: event.eventType,
              conversion_id: conversionId,
            });
            attemptedDelivery = true;
            window.lintrk("track", { conversion_id: conversionId });
            console.log(
              "[TRACKING DEBUG] LinkedIn conversion event sent successfully"
            );
          } else {
            console.log(
              "[TRACKING DEBUG] Skipping LinkedIn - conversion ID is null for event type:",
              event.eventType
            );
          }
        } else {
          console.log("[TRACKING DEBUG] LinkedIn lintrk not available");
        }

        // Mark as delivered if at least one provider attempted
        if (attemptedDelivery) {
          console.log("[TRACKING DEBUG] Marking event as delivered:", event.id);
          deliveredIds.push(event.id);
        } else {
          console.log(
            "[TRACKING DEBUG] Event not delivered - no providers attempted:",
            event.id
          );
        }
      }

      console.log(
        "[TRACKING DEBUG] Total events processed:",
        trkEvents.length,
        "delivered:",
        deliveredIds.length
      );

      // Ack delivered events
      if (deliveredIds.length > 0) {
        console.log(
          "[TRACKING DEBUG] Acknowledging delivered events:",
          deliveredIds
        );
        try {
          await ky.post("/api/tracking/ack", {
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ ids: deliveredIds }),
          });
          console.log(
            "[TRACKING DEBUG] Successfully acknowledged",
            deliveredIds.length,
            "events"
          );
        } catch (error) {
          console.error(
            "[TRACKING DEBUG] Failed to ack tracking events:",
            error
          );
          // Do nothing - cookie will retry next load
        }
      } else {
        console.log("[TRACKING DEBUG] No events to acknowledge");
      }
    };

    flushEvents();
  }, [isProd, trkEvents]);

  // Extensions stamp attributes on <html>/<body> before hydration.
  // suppressHydrationWarning mutes that noise, but it also means a genuine
  // className mismatch on <body> would go unwarned, so the layout classes on
  // these two elements must stay server-rendered and static.
  return (
    <html lang={lang} suppressHydrationWarning>
      <head>
        <Meta />
        {/* Without this, tablets lay the app out at 980px and scale it down,
            which pushes the Live Audit controls off the right edge. It lives
            here rather than in meta() because route meta() exports replace
            the root's. */}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Links />
        {isProd && (
          <>
            {/* Google Ads */}
            <script
              async
              src={`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`}
            />
            <script
              dangerouslySetInnerHTML={{
                __html: `
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments);}
                  gtag('js', new Date());
                  gtag('config', '${GOOGLE_ADS_ID}');
                `,
              }}
            />
            {/* LinkedIn Insight Tag */}
            <script
              type="text/javascript"
              dangerouslySetInnerHTML={{
                __html: `
                  _linkedin_partner_id = "${LINKEDIN_PARTNER_ID}";
                  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
                  window._linkedin_data_partner_ids.push(_linkedin_partner_id);
                  (function(l) {
                    if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
                    window.lintrk.q=[]}
                    var s = document.getElementsByTagName("script")[0];
                    var b = document.createElement("script");
                    b.type = "text/javascript";b.async = true;
                    b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
                    s.parentNode.insertBefore(b, s);})(window.lintrk);
                `,
              }}
            />
            <noscript>
              <img
                height="1"
                width="1"
                style={{ display: "none" }}
                alt=""
                src={`https://px.ads.linkedin.com/collect/?pid=${LINKEDIN_PARTNER_ID}&fmt=gif`}
              />
            </noscript>
          </>
        )}
      </head>
      <body className="flex flex-col min-h-screen" suppressHydrationWarning>
        <QueryClientProvider client={queryClient}>
          <div className="flex-1 flex flex-col">
            <Outlet />
          </div>
          {!isLoggedIn && <Footer />}
          <Toaster />
        </QueryClientProvider>
        {ENV && (
          <script
            dangerouslySetInnerHTML={{
              __html: `window.ENV = ${JSON.stringify(ENV)}`,
            }}
          />
        )}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export const ErrorBoundary = RouteErrorBoundary;

export default withSentry(App);
